import { Component, OnInit, Input } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';

import { FruHttpService } from '../../../../../services/fru-http.service';
import { GeneralResponse } from '../../../../../shared/general-response';
import { PremiumAnswers } from '../../../../../shared/premium-answers';
import { PremiumQuestions } from '../../../../../shared/premium-questions';

@Component({
	selector: 'premium-lead-form',
	templateUrl: './premium-lead-form.component.html',
	styleUrls: ['./premium-lead-form.component.scss']
})

export class PremiumLeadFormComponent implements OnInit {
	@Input() leadId: string;
	@Input() premiumQuestions: PremiumQuestions;

	private premiumAnswers: PremiumAnswers;
	public premiumLeadForm: UntypedFormGroup;
	public premiumLeadControls: object = {};
	public premiumQuestionKeys: string[] = [];
	public disableSubmit: boolean = false;
	public invalidSubmission: boolean = false;
	public responseErrors: object = {};

	constructor(private formBuilder: UntypedFormBuilder, private fruHttp: FruHttpService) { }

	ngOnInit() {
		// Get question keys
		this.premiumQuestionKeys = Object.keys(this.premiumQuestions);
		// Build form
		this.buildForm();
	}

	buildForm() {
		for (let key in this.premiumQuestions) {
			// Create and save form control
			this.premiumLeadControls[key] = this.formBuilder.control('', this.premiumQuestions[key]['hidden'] ? undefined : Validators.required)
			// Assign an icon for each question
			this.assignIcons(key);
		}

		// Group form controls
		this.premiumLeadForm = this.formBuilder.group(this.premiumLeadControls);
	}

	submitControl(key: string, value: string) {
		this.premiumLeadControls[key].setValue(value);

		switch (key) {
			case 'lowerPaymentRate':
				this.runCondtions('lowerPaymentRate', 'No', { key: 'cashOut', hidden: false, children: ['whyRefinance'] });
				this.runCondtions('lowerPaymentRate', 'Yes', { key: 'toldRateLow', hidden: false }, true);
				break;
			case 'cashOut':
				this.runCondtions('cashOut', 'No', { key: 'whyRefinance', hidden: false });
				this.runCondtions('cashOut', 'No', { key: 'toldRateLow', hidden: false });
				break;
		}
	}

	runCondtions(key: string, value: string, result: any, skipElse: boolean = false) {
		let control: UntypedFormControl = this.premiumLeadControls[key];

		if ((control.touched || control.dirty || control.valid) && control.value !== '') {
			if (control.value === value) {
				this.premiumQuestions[result.key]['hidden'] = result.hidden;

				// The resulting question must be required now
				if (this.premiumLeadControls[result.key]['errors'] === null) {
					this.premiumLeadControls[result.key] = this.formBuilder.control(this.premiumLeadControls[result.key]['value'], Validators.required);
					this.premiumLeadForm.setControl(result.key, this.premiumLeadControls[result.key]);
				}
			} else if (! skipElse) {
				// Reset the conditional question, along with any questions that depend on it
				result.children = result.children !== undefined ? result.children : [];
				result.children.push(result.key);

				for (let i in result.children) {
					let k: string = result.children[i];
					this.premiumQuestions[k]['hidden'] = ! result.hidden;
					this.premiumLeadControls[k] = this.formBuilder.control('', undefined);
					this.premiumLeadForm.setControl(k, this.premiumLeadControls[k]);
				}
			}
		}
	}

	submitForm(premiumAnswers) {
		this.disableSubmit = true;

		for (let key in premiumAnswers) {
			if (premiumAnswers[key] === '' && this.premiumQuestions[key]['hidden']) {
				delete(premiumAnswers[key]);
			}
		}

		// Assign form values
		this.premiumAnswers = { request_id: this.leadId, answers: premiumAnswers };

		// Submit premium answers
		this.fruHttp
			.submitPremiumAnswers(this.premiumAnswers)
			.subscribe(response => {
				this.handleResponse(response);
			});
	}

	handleResponse(response: GeneralResponse) {
		if (response === null || response.status === 'error') {
			if (response !== null) delete(response.data);
			this.invalidSubmission = true;
			this.responseErrors = response !== null ? response : { status: 'error', message: 'Invalid submission.'};
			setTimeout(() => { this.invalidSubmission = false; }, 5000);
		} else if (response.status === 'success' && Object.keys(response.data).length > 0) {
			this.fruHttp.redirectThankYouNoLead(response.data);
		}
	}

	assignIcons(key: string) {
		switch (key) {
			case 'loanAmount':
			case 'propertyValue':
			case 'newHomeValue':
				this.premiumQuestions[key]['icon'] = 'fas fa-dollar-sign';
				break;
			case 'creditRating':
				this.premiumQuestions[key]['icon'] = 'fas fa-credit-card';
				break;
			case 'fewToTalk':
				this.premiumQuestions[key]['icon'] = 'far fa-clock';
				break;
			case 'cashOut':
				this.premiumQuestions[key]['icon'] = 'fas fa-donate';
				break;
			case 'lowerPaymentRate':
				this.premiumQuestions[key]['icon'] = 'far fa-money-bill-alt';
				break;
			case 'whyRefinance':
			case 'justThinking':
				this.premiumQuestions[key]['icon'] = 'fas fa-question';
				break;
			case 'toldRateLow':
			case 'toldNotQualify':
				this.premiumQuestions[key]['icon'] = 'fas fa-exclamation-triangle';
				break;
			case 'downPaymentSource':
				this.premiumQuestions[key]['icon'] = 'fas fa-percentage';
				break;
			case 'daysFromBuying':
				this.premiumQuestions[key]['icon'] = 'fas fa-shopping-cart';
				break;
			case 'foundHome':
				this.premiumQuestions[key]['icon'] = 'fas fa-search';
				break;
			case 'signedAgreement':
				this.premiumQuestions[key]['icon'] = 'fas fa-file-signature';
				break;
			case 'verifyEmail':
				this.premiumQuestions[key]['icon'] = 'fas fa-envelope';
				break;
			case 'permanentFoundation':
				this.premiumQuestions[key]['icon'] = 'fas fa-lock';
				break;
			default:
				this.premiumQuestions[key]['icon'] = 'fas fa-home';
				break;
		}
	}

	/*
	getButtonClass(key: string, button: any) {
		let buttonClass: string = 'is-light';

		if (this.premiumLeadControls[key]['value'] === button.text) {
			buttonClass = 'is-active ';

			if (button.nextQuestion !== false) {
				buttonClass += 'is-success';
			} else {
				buttonClass += 'is-warning';
			}
		}

		return buttonClass;
	}
	*/
}
